/**
 * Разрешение на уведомления как сценарий (CLIENT_WEB.md §8.1, экраны N-03/N-04).
 *
 * Правило разрешений (ПС §18): системный запрос вызывается ТОЛЬКО из
 * обработчика жеста пользователя, поэтому хук не запрашивает ничего сам —
 * он лишь отдаёт состояние и функцию, которую экран вызывает по нажатию.
 */
import { useCallback, useEffect, useState } from 'react'

import type { Config } from '@shared/api/types'
import { hintsRepo } from '@shared/db'
import { enablePush, pushState, type EnableOutcome, type PushState } from '@shared/push'

export interface PushPermission {
  state: PushState
  /** Идёт системный диалог или регистрация подписки. */
  busy: boolean
  /** Можно предложить включение: движок поддерживает и сервер разрешает. */
  canOffer: boolean
  enable: () => Promise<EnableOutcome>
  refresh: () => void
}

export function usePushPermission(config: Config | null): PushPermission {
  const [state, setState] = useState<PushState>(() => pushState(config))
  const [busy, setBusy] = useState(false)

  const refresh = useCallback(() => setState(pushState(config)), [config])

  useEffect(() => {
    refresh()
  }, [refresh])

  /*
   * Разрешение могли изменить в настройках браузера, не трогая приложение, —
   * состояние пересчитывается при возврате вкладки.
   */
  useEffect(() => {
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') refresh()
    }

    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [refresh])

  const enable = useCallback(async (): Promise<EnableOutcome> => {
    setBusy(true)
    try {
      const outcome = await enablePush(config)
      setState(pushState(config))

      // §8.1 п. 3: после отказа повторный системный запрос невозможен — N-04.
      if (outcome === 'denied') await hintsRepo.markSeen('push_denied')
      return outcome
    } finally {
      setBusy(false)
    }
  }, [config])

  return {
    state,
    busy,
    canOffer: state === 'prompt',
    enable,
    refresh,
  }
}
